import React from "react";
import { motion } from "framer-motion";
import {
  BookOpen,
  CheckCircle2,
  Calendar,
  Clock,
  Monitor,
  Users,
  Target,
  Presentation,
  Award,
  FileText,
  Sparkles,
  Brain,
  GraduationCap,
} from "lucide-react";

const ProgramDetails: React.FC = () => {
  return (
    <motion.section
      id="program-details"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className="py-20 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Program Details
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive information about our Cambridge Research Programmes
          </p>
        </motion.div>

        <div className="space-y-16">
          {/* 1. Teaching Methodology & Academic Governance */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 md:p-10 shadow-lg"
          >
            <div className="flex items-center mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mr-4 shadow-md">
                <BookOpen className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                Teaching Methodology & Academic Governance
              </h3>
            </div>
            <p className="text-gray-700 text-lg mb-6 leading-relaxed">
              Our hybrid model ensures students receive world-class education
              with continuous on-site support.
            </p>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-blue-500">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                  <CheckCircle2 className="h-5 w-5 text-blue-500 mr-2" />
                  Academic Design
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  The entire curriculum, syllabus, and assessment criteria are
                  designed and supervised directly by University of Cambridge
                  Professors.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-indigo-500">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                  <CheckCircle2 className="h-5 w-5 text-indigo-500 mr-2" />
                  Hybrid Leadership
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  While Professors deliver lectures via live online sessions (and
                  selected in-person appearances), daily on-site instruction and
                  mentoring are led by Cambridge PhD female researchers.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-purple-500">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                  <CheckCircle2 className="h-5 w-5 text-purple-500 mr-2" />
                  Boutique Class Sizes
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  To ensure personalised academic supervision, class sizes are
                  strictly limited to a maximum of 17 students per stream.
                </p>
              </div>
            </div>
          </motion.div>

          {/* 2. The 12-Day Academic Itinerary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 md:p-10 shadow-lg"
          >
            <div className="flex items-center mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mr-4 shadow-md">
                <Calendar className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                The 12-Day Academic Itinerary
              </h3>
            </div>
            <p className="text-gray-700 text-lg mb-6 leading-relaxed">
              A step-by-step breakdown of the student experience:
            </p>
            <div className="space-y-6">
              {/* Days 1-2 */}
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h4 className="font-bold text-lg text-gray-900 mb-3 flex items-center">
                  <Clock className="h-5 w-5 text-purple-500 mr-2" />
                  Days 1–2: Foundations & Research Skills
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="font-semibold mr-2">Live Online:</span>
                    <span>
                      Welcome session with the supervising Cambridge Professor.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-semibold mr-2 whitespace-nowrap">
                      In-Person:
                    </span>
                    <span>
                      "Research Methods" training led by Cambridge PhD
                      researchers (covering hypothesis formulation, source
                      evaluation, and academic integrity).
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-semibold mr-2">Activity:</span>
                    <span>
                      Students are assigned to small research groups (approx. 5
                      students).
                    </span>
                  </li>
                </ul>
              </div>

              {/* Days 3-4 */}
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h4 className="font-bold text-lg text-gray-900 mb-3 flex items-center">
                  <Monitor className="h-5 w-5 text-purple-500 mr-2" />
                  Days 3–4: Professor-Led Theory (Online)
                </h4>
                <p className="text-gray-700">
                  Live online interactive lectures delivered by the Cambridge
                  Professor to establish theoretical foundations.
                </p>
              </div>

              {/* Day 5 */}
              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-purple-500">
                <h4 className="font-bold text-lg text-gray-900 mb-3 flex items-center">
                  <Users className="h-5 w-5 text-purple-500 mr-2" />
                  Day 5: The In-Person Professor Session
                </h4>
                <p className="text-gray-700">
                  A special in-person teaching day where the Cambridge Professor
                  joins the students in the classroom for applied analysis and
                  case studies.
                </p>
              </div>

              {/* Days 6-7 */}
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h4 className="font-bold text-lg text-gray-900 mb-3 flex items-center">
                  <Clock className="h-5 w-5 text-purple-500 mr-2" />
                  Days 6–7: Weekend Break
                </h4>
                <p className="text-gray-700">
                  No formal teaching; optional independent study.
                </p>
              </div>

              {/* Days 8-11 */}
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h4 className="font-bold text-lg text-gray-900 mb-3 flex items-center">
                  <Target className="h-5 w-5 text-purple-500 mr-2" />
                  Days 8–11: Project Development (Mentorship Phase)
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="font-semibold mr-2">Focus:</span>
                    <span>
                      The Professor does not teach during this phase. Students
                      work entirely under the supervision of on-site PhD
                      researchers.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-semibold mr-2">Activity:</span>
                    <span>
                      Groups structure their arguments, design their research
                      projects, and rehearse presentations for the final jury.
                    </span>
                  </li>
                </ul>
              </div>

              {/* Day 12 */}
              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-pink-500">
                <h4 className="font-bold text-lg text-gray-900 mb-3 flex items-center">
                  <Presentation className="h-5 w-5 text-pink-500 mr-2" />
                  Day 12: Final Presentations & Assessment
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="font-semibold mr-2">The Jury:</span>
                    <span>
                      The Cambridge Professor joins via live link as the Lead
                      Examiner, while the PhD researcher acts as the on-site
                      Moderator.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-semibold mr-2">The Exam:</span>
                    <span>
                      Groups deliver 10–15 minute academic presentations
                      followed by a Q&A defence.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* 3. Certification & Competitive Awards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-8 md:p-10 shadow-lg"
          >
            <div className="flex items-center mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center mr-4 shadow-md">
                <Award className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                Certification & Competitive Awards
              </h3>
            </div>
            <p className="text-gray-700 text-lg mb-6 leading-relaxed">
              We offer tangible outcomes for university portfolios:
            </p>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-yellow-500">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                  <FileText className="h-5 w-5 text-yellow-500 mr-2" />
                  Certificate of Attendance
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Awarded to all students who complete the programme, signed by
                  the Cambridge Professor (including their academic title).
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-orange-500">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                  <Sparkles className="h-5 w-5 text-orange-500 mr-2" />
                  Letter of Recommendation (Merit-Based)
                </h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  This is an exclusive accolade awarded only to the top 50% of
                  students in each class.
                </p>
                <div className="bg-orange-50 rounded-lg p-4 border-l-4 border-orange-400">
                  <p className="text-sm font-semibold text-gray-800 mb-1">
                    Criteria:
                  </p>
                  <p className="text-gray-700 text-sm">
                    Selection is based on academic performance, engagement
                    during sessions, and the quality of the final presentation.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* 4. Available Subject Streams */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 md:p-10 shadow-lg"
          >
            <div className="flex items-center mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mr-4 shadow-md">
                <Brain className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                Available Subject Streams
              </h3>
            </div>
            <p className="text-gray-700 text-lg mb-6 leading-relaxed">
              Students must apply for one of the following specialist streams:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  number: "1",
                  title: "Entrepreneurship",
                  description:
                    "Innovation and business modelling (aligned with the Cambridge Judge Business School expertise).",
                },
                {
                  number: "2",
                  title: "Artificial Intelligence (AI)",
                  description: "Machine learning, ethics, and technology.",
                },
                {
                  number: "3",
                  title: "Medicine",
                  description:
                    "Biomedical and clinical research methodologies.",
                },
              ].map((stream, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: 0.4 + index * 0.1,
                  }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center mb-4 shadow-md">
                    <span className="text-2xl font-bold text-white">
                      {stream.number}
                    </span>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">
                    {stream.title}
                  </h4>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {stream.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* 5. Applicant Profile */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 md:p-10 shadow-lg"
          >
            <div className="flex items-center mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mr-4 shadow-md">
                <GraduationCap className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                Applicant Profile
              </h3>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-cyan-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">
                      Target Audience
                    </h4>
                    <p className="text-gray-700">
                      High-achieving students aged 15–18.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">
                      Requirements
                    </h4>
                    <p className="text-gray-700">
                      Strong academic motivation and a willingness to engage in
                      university-level critical thinking and research.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default ProgramDetails;
